import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { TeenBusinessListingApplicationListItemResponse } from "../../../types/businessListingTypes";
import { formatBusinessListingCompensation, formatBusinessListingDuration, formatBusinessListingJobType } from "../../../utils/BusinessListingUtils";
import { formatDateTime } from "../../../utils/DateUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";
import BusinessListingApplicationMessagesPanel from "../../business-listings/BusinessListingApplicationMessagesPanel";
import Pagination from "../../gigs/Pagination";
import Button from "../../ui/Button/Button";
import InfoTooltip from "../../ui/InfoTooltip/InfoTooltip";
import Modal from "../../ui/Modal/Modal";
import PageActionBar from "../../ui/Page/PageActionBar";

type TeenBusinessListingApplicationsSectionProps = {
  applications: TeenBusinessListingApplicationListItemResponse[];
  search: string;
  onSearchChange: (value: string) => void;
  onWithdrawApplication: (applicationId: string) => void | Promise<void>;
  pendingWithdrawApplicationId: string | null;
  onReapplyApplication: (applicationId: string) => void | Promise<void>;
  pendingReapplyApplicationId: string | null;
};

type TeenBusinessApplicationDisplayStatus = "APPLIED" | "WITHDRAWN" | "REJECTED" | "EXPIRED";

const applicationStatusLabel: Record<TeenBusinessApplicationDisplayStatus, string> = {
  APPLIED: "Applied",
  WITHDRAWN: "Withdrawn",
  REJECTED: "Rejected",
  EXPIRED: "Expired",
};

const applicationStatusHelpText: Record<TeenBusinessApplicationDisplayStatus, string> = {
  APPLIED: "Your application is still active. You can review details, message the business, and withdraw while the listing remains open.",
  WITHDRAWN: "You withdrew this application. It stays in your history, and you can apply again if the listing is still open.",
  REJECTED: "The business decided not to move forward with this application. It stays here as part of your application history.",
  EXPIRED: "The listing expired and is no longer accepting applications. Your application remains here for reference.",
};

const getDisplayStatus = (
  application: TeenBusinessListingApplicationListItemResponse,
): TeenBusinessApplicationDisplayStatus => {
  if (application.listingStatus === "EXPIRED") {
    return "EXPIRED";
  }
  if (application.applicationStatus === "WITHDRAWN") {
    return "WITHDRAWN";
  }
  if (application.applicationStatus === "REJECTED") {
    return "REJECTED";
  }
  return "APPLIED";
};

const canMessageOnApplication = (application: TeenBusinessListingApplicationListItemResponse) => {
  return getDisplayStatus(application) === "APPLIED";
};

const canWithdrawApplication = (application: TeenBusinessListingApplicationListItemResponse) => {
  return getDisplayStatus(application) === "APPLIED";
};

const canReapplyToApplication = (application: TeenBusinessListingApplicationListItemResponse) => {
  return application.applicationStatus === "WITHDRAWN" && application.listingStatus === "PUBLISHED" && Boolean(application.listingId);
};

const getInactiveThreadMessage = (application: TeenBusinessListingApplicationListItemResponse) => {
  const displayStatus = getDisplayStatus(application);

  if (displayStatus === "WITHDRAWN") {
    return "You withdrew this application. This thread is no longer active.";
  }
  if (displayStatus === "REJECTED") {
    return "This application was rejected. This thread is no longer active.";
  }
  if (displayStatus === "EXPIRED") {
    return "This listing expired. This thread is no longer active.";
  }
  return "This application is no longer active.";
};

const PAGE_SIZE_OPTIONS = [5, 10, 20] as const;
type ApplicationStatusFilter = "ALL" | TeenBusinessApplicationDisplayStatus;

const TeenBusinessApplicationDetailsContent = ({
  application,
}: {
  application: TeenBusinessListingApplicationListItemResponse;
}) => (
  <div className="grid gap-4">
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="min-w-0 rounded-2xl bg-myteengig-background/70 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Business</p>
        <p className="mt-2 text-sm text-myteengig-text-primary">{application.businessName ?? "Business"}</p>
      </div>
      <div className="min-w-0 rounded-2xl bg-myteengig-background/70 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Status</p>
        <p className="mt-2 text-sm text-myteengig-text-primary">{applicationStatusLabel[getDisplayStatus(application)]}</p>
      </div>
      <div className="min-w-0 rounded-2xl bg-myteengig-background/70 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Job type</p>
        <p className="mt-2 text-sm text-myteengig-text-primary">
          {application.jobType ? formatBusinessListingJobType(application.jobType) : "Not set"}
        </p>
      </div>
      <div className="min-w-0 rounded-2xl bg-myteengig-background/70 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Usual shift</p>
        <p className="mt-2 text-sm text-myteengig-text-primary">
          {formatBusinessListingDuration(application.estimatedDurationMinutes) ?? "Not set"}
        </p>
      </div>
      <div className="min-w-0 rounded-2xl bg-myteengig-background/70 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Location</p>
        <p className="mt-2 text-sm text-myteengig-text-primary">{formatCityStateZip(application, "Location not set")}</p>
      </div>
      <div className="min-w-0 rounded-2xl bg-myteengig-background/70 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Compensation</p>
        <p className="mt-2 text-sm text-myteengig-text-primary">
          {application.payType && application.minPayAmountCents !== null && application.maxPayAmountCents !== null
            ? formatBusinessListingCompensation(
              application.payType,
              application.minPayAmountCents,
              application.maxPayAmountCents,
              application.jobType,
            )
            : "Not set"}
        </p>
      </div>
    </div>

    <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Listing description</p>
      <p className="mt-2 text-sm leading-6 text-myteengig-text-primary">
        {application.listingDescription ?? "No description provided."}
      </p>
    </div>

    <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Your application note</p>
      <p className="mt-2 text-sm leading-6 text-myteengig-text-primary">
        {application.applicationMessage ?? "No message provided."}
      </p>
    </div>

    {application.resume ? (
      <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Resume</p>
        <a
          href={application.resume.url}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex text-sm font-medium text-myteengig-primary hover:underline"
        >
          {application.resume.originalFileName ?? "Download resume"}
        </a>
      </div>
    ) : null}
  </div>
);

const TeenBusinessListingApplicationsSection = ({
  applications,
  search,
  onSearchChange,
  onWithdrawApplication,
  pendingWithdrawApplicationId,
  onReapplyApplication,
  pendingReapplyApplicationId,
}: TeenBusinessListingApplicationsSectionProps) => {
  const [searchParams] = useSearchParams();
  const [detailsModalApplicationId, setDetailsModalApplicationId] = useState<string | null>(null);
  const [messageModalApplicationId, setMessageModalApplicationId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ApplicationStatusFilter>("ALL");
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState(0);
  const [withdrawConfirmationApplication, setWithdrawConfirmationApplication] =
    useState<TeenBusinessListingApplicationListItemResponse | null>(null);
  const highlightedApplicationId = searchParams.get("applicationId");
  const highlightedSection = searchParams.get("section");

  const filteredApplications = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return applications.filter((application) => {
      const matchesStatus = statusFilter === "ALL" || getDisplayStatus(application) === statusFilter;
      if (!matchesStatus) {
        return false;
      }

      if (normalizedSearch.length === 0) {
        return true;
      }

      return [
        application.listingTitle,
        application.businessName,
        application.category,
        application.city,
        application.state,
      ].some((value) => value?.toLowerCase().includes(normalizedSearch));
    });
  }, [applications, search, statusFilter]);

  const pageCount = Math.ceil(filteredApplications.length / pageSize);
  const safePage = pageCount === 0 ? 0 : Math.min(page, pageCount - 1);
  const paginatedApplications = filteredApplications.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize,
  );
  const hasPreviousPage = safePage > 0;
  const hasNextPage = safePage + 1 < pageCount;

  useEffect(() => {
    if (!highlightedApplicationId) {
      return;
    }
    if (highlightedSection === "messages") {
      setMessageModalApplicationId(highlightedApplicationId);
      setDetailsModalApplicationId(null);
      return;
    }
    setDetailsModalApplicationId(highlightedApplicationId);
    setMessageModalApplicationId(null);
  }, [highlightedApplicationId, highlightedSection]);

  useEffect(() => {
    if (!highlightedApplicationId) {
      return;
    }
    const highlightedIndex = filteredApplications.findIndex((application) => application.applicationId === highlightedApplicationId);
    if (highlightedIndex >= 0) {
      setPage(Math.floor(highlightedIndex / pageSize));
    }
  }, [filteredApplications, highlightedApplicationId, pageSize]);

  useEffect(() => {
    if (!highlightedApplicationId) {
      return;
    }
    const highlightedCard = document.getElementById(`teen-business-application-${highlightedApplicationId}`);
    if (highlightedCard) {
      highlightedCard.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedApplicationId, paginatedApplications]);

  const handleConfirmWithdraw = async () => {
    if (!withdrawConfirmationApplication) {
      return;
    }

    await onWithdrawApplication(withdrawConfirmationApplication.applicationId);
    setWithdrawConfirmationApplication(null);
  };

  const detailsModalApplication = applications.find(
    (application) => application.applicationId === detailsModalApplicationId,
  ) ?? null;
  const messageModalApplication = applications.find(
    (application) => application.applicationId === messageModalApplicationId,
  ) ?? null;

  return (
    <>
      <Modal
        isOpen={detailsModalApplication !== null}
        onClose={() => setDetailsModalApplicationId(null)}
        title={detailsModalApplication?.listingTitle ?? "Application Details"}
        panelClassName="max-w-5xl"
        bodyClassName="overflow-y-auto"
      >
        {detailsModalApplication ? <TeenBusinessApplicationDetailsContent application={detailsModalApplication} /> : null}
      </Modal>

      <Modal
        isOpen={messageModalApplication !== null}
        onClose={() => setMessageModalApplicationId(null)}
        title={messageModalApplication ? `${messageModalApplication.listingTitle ?? "Application"} · Messages` : "Messages"}
        panelClassName="max-w-5xl h-[min(90vh,52rem)]"
        bodyClassName="h-full"
      >
        {messageModalApplication ? (
          canMessageOnApplication(messageModalApplication) ? (
            <BusinessListingApplicationMessagesPanel
              applicationId={messageModalApplication.applicationId}
              role="teen"
              participantLabel={messageModalApplication.businessName ?? "business"}
              description={`Message ${messageModalApplication.businessName ?? "the business"} here to discuss fit, terms, and next steps.`}
              variant="bare"
              fullHeight
            />
          ) : (
            <div className="rounded-2xl border border-myteengig-border bg-white px-4 py-4 text-sm text-myteengig-text-muted">
              {getInactiveThreadMessage(messageModalApplication)}
            </div>
          )
        ) : null}
      </Modal>

      <section className="mt-6 rounded-[1.75rem] border border-myteengig-border bg-white p-4 shadow-sm sm:p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(16rem,1.2fr)_10rem_5.25rem] lg:items-end">
          <div className="sm:col-span-2 lg:col-span-1">
            <label htmlFor="teen-business-applications-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
              Search
            </label>
            <input
              id="teen-business-applications-search"
              type="text"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Listing title, business, category, city, state"
              className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-base text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
            />
          </div>
          <div>
            <label htmlFor="teen-business-applications-status" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
              Status
            </label>
            <select
              id="teen-business-applications-status"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value as ApplicationStatusFilter);
                setPage(0);
              }}
              className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
            >
              <option value="ALL">All statuses</option>
              <option value="APPLIED">Applied</option>
              <option value="EXPIRED">Expired</option>
              <option value="REJECTED">Rejected</option>
              <option value="WITHDRAWN">Withdrawn</option>
            </select>
          </div>
          <div className="hidden md:block">
            <label htmlFor="teen-business-applications-page-size" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
              Rows
            </label>
            <select
              id="teen-business-applications-page-size"
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}
              className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {filteredApplications.length === 0 ? (
        <section className="mt-6 rounded-[1.75rem] border border-dashed border-myteengig-border bg-white px-6 py-10 text-center">
          <p className="text-lg font-semibold text-myteengig-text-primary">
            {applications.length === 0 ? "No business listing applications yet" : "No matching business listing applications"}
          </p>
          <p className="mt-2 text-sm text-myteengig-text-muted">
            {applications.length === 0
              ? "Apply from the Business Jobs view under Opportunities and the thread will appear here."
              : "Adjust your search to review the rest of your business listing applications."}
          </p>
        </section>
      ) : (
        <div className="mt-6 grid gap-4">
          {paginatedApplications.map((application) => {
            const isHighlighted = application.applicationId === highlightedApplicationId;
            const displayStatus = getDisplayStatus(application);
            const canMessage = canMessageOnApplication(application);
            const canWithdraw = canWithdrawApplication(application);
            const canReapply = canReapplyToApplication(application);
            const isWithdrawing = pendingWithdrawApplicationId === application.applicationId;
            const isReapplying = pendingReapplyApplicationId === application.applicationId;
            const isShowingDetails = detailsModalApplicationId === application.applicationId;
            const isShowingMessages = messageModalApplicationId === application.applicationId;

            return (
              <article
                key={application.applicationId}
                id={`teen-business-application-${application.applicationId}`}
                className={`rounded-[1.5rem] border p-5 transition ${
                  isHighlighted
                    ? "border-myteengig-primary bg-[linear-gradient(180deg,_rgba(239,248,255,1),_rgba(255,255,255,1))] ring-2 ring-myteengig-primary/20"
                    : "border-myteengig-border bg-white"
                }`}
              >
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <h2 className="text-lg font-semibold text-myteengig-text-primary">
                            {application.listingTitle ?? "Unknown business listing"}
                          </h2>
                          <div className="flex w-full items-center justify-center gap-1 sm:w-auto sm:justify-start">
                            <span className="inline-flex items-center justify-center rounded-full border border-myteengig-border bg-white px-3 py-1 text-center text-xs font-semibold text-myteengig-text-primary">
                              {applicationStatusLabel[displayStatus]}
                            </span>
                            <InfoTooltip
                              message={applicationStatusHelpText[displayStatus]}
                              label={`${applicationStatusLabel[displayStatus]} status info`}
                              placement="top-center"
                              className="shrink-0"
                            />
                          </div>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-3 text-sm text-myteengig-text-muted">
                          <span>{application.businessName ?? "Business"}</span>
                          <span>{application.category ?? "Category not set"}</span>
                          <span>{formatCityStateZip(application, "Location not set")}</span>
                          {application.payType && application.minPayAmountCents !== null && application.maxPayAmountCents !== null ? (
                            <span className="hidden sm:inline">
                              {formatBusinessListingCompensation(
                                application.payType,
                                application.minPayAmountCents,
                                application.maxPayAmountCents,
                                application.jobType,
                              )}
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-3 hidden flex-wrap items-center gap-3 text-sm text-myteengig-text-muted sm:flex">
                          <span>Applied {formatDateTime(application.createdAt)}</span>
                          {application.publishedAt ? <span>Posted {formatDateTime(application.publishedAt)}</span> : null}
                        </div>
                      </div>

                      <div className="hidden sm:flex sm:flex-wrap sm:items-center sm:justify-end sm:gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={isShowingMessages ? "primary" : "outline"}
                          onClick={() => {
                            setMessageModalApplicationId(application.applicationId);
                            setDetailsModalApplicationId(null);
                          }}
                          disabled={!canMessage}
                          className="sm:w-auto"
                        >
                          Message
                        </Button>
                        {canWithdraw ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setWithdrawConfirmationApplication(application)}
                            disabled={isWithdrawing}
                            className="sm:w-auto"
                          >
                            {isWithdrawing ? "Withdrawing..." : "Withdraw"}
                          </Button>
                        ) : null}
                        {canReapply ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => void onReapplyApplication(application.applicationId)}
                            disabled={isReapplying}
                            className="sm:w-auto"
                          >
                            {isReapplying ? "Applying..." : "Apply Again"}
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          variant={isShowingDetails ? "primary" : "outline"}
                          onClick={() => {
                            setDetailsModalApplicationId(application.applicationId);
                            setMessageModalApplicationId(null);
                          }}
                          className="sm:w-auto"
                        >
                          View Details
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-2 sm:hidden">
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={isShowingDetails ? "primary" : "outline"}
                          onClick={() => {
                            setDetailsModalApplicationId(application.applicationId);
                            setMessageModalApplicationId(null);
                          }}
                          className="w-full"
                        >
                          View Details
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={isShowingMessages ? "primary" : "outline"}
                          onClick={() => {
                            setMessageModalApplicationId(application.applicationId);
                            setDetailsModalApplicationId(null);
                          }}
                          disabled={!canMessage}
                          className="w-full text-center"
                        >
                          Message
                        </Button>
                      </div>
                      {canWithdraw ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setWithdrawConfirmationApplication(application)}
                          disabled={isWithdrawing}
                          className="w-full"
                        >
                          {isWithdrawing ? "Withdrawing..." : "Withdraw"}
                        </Button>
                      ) : null}
                      {canReapply ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => void onReapplyApplication(application.applicationId)}
                          disabled={isReapplying}
                          className="w-full"
                        >
                          {isReapplying ? "Applying..." : "Apply Again"}
                        </Button>
                      ) : null}
                    </div>

                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={withdrawConfirmationApplication !== null}
        onClose={() => {
          if (!pendingWithdrawApplicationId) {
            setWithdrawConfirmationApplication(null);
          }
        }}
        title="Withdraw Business Application?"
      >
        <div className="space-y-5">
          <div className="rounded-2xl bg-myteengig-background px-4 py-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">
              {withdrawConfirmationApplication?.listingTitle ?? "This business listing"}
            </p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              This removes your application from the active business listing flow and closes the message thread.
            </p>
          </div>

          <p className="text-sm text-myteengig-text-muted">Are you sure you want to withdraw this application?</p>

          <PageActionBar align="end">
            <Button
              variant="outline"
              disabled={pendingWithdrawApplicationId !== null}
              onClick={() => setWithdrawConfirmationApplication(null)}
              className="w-full sm:w-auto"
            >
              Keep Application
            </Button>
            <Button
              disabled={pendingWithdrawApplicationId !== null}
              onClick={() => void handleConfirmWithdraw()}
              className="w-full sm:w-auto"
            >
              {pendingWithdrawApplicationId !== null ? "Withdrawing..." : "Yes, Withdraw"}
            </Button>
          </PageActionBar>
        </div>
      </Modal>

      <Pagination
        page={safePage}
        pageSize={pageSize}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        resultsCount={paginatedApplications.length}
        onPageChange={setPage}
      />
    </>
  );
};

export default TeenBusinessListingApplicationsSection;
